import React, { useState, useEffect } from 'react';
import { TrendingDown, DollarSign, Users, Loader2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuickPriceDropModal = ({ isOpen, onClose, product, userId, onPriceUpdated }) => {
  const [newPrice, setNewPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);

  useEffect(() => {
    if (isOpen && product) {
      setNewPrice(Math.floor(product.price * 0.9).toString());
      fetchSuggestion();
    }
  }, [isOpen, product]);

  const fetchSuggestion = async () => {
    if (!product?.id) return;
    setLoadingSuggestion(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${product.id}/price-suggestion`);
      if (response.ok) {
        const data = await response.json();
        setSuggestion(data);
      }
    } catch (error) {
      console.error('Failed to fetch suggestion:', error);
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleQuickDrop = async (percentage) => {
    const droppedPrice = Math.floor(product.price * (1 - percentage / 100));
    setNewPrice(droppedPrice.toString());
  };

  const handleSubmit = async () => {
    const price = parseFloat(newPrice);
    if (!price || price >= product.price) {
      toast.error('New price must be lower than current price');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/quick-price-drop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.id,
          new_price: price,
          seller_id: userId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(
          data.watchers_notified > 0 
            ? `Price dropped! ${data.watchers_notified} watchers notified 🎉`
            : 'Price dropped successfully!'
        );
        if (onPriceUpdated) {
          onPriceUpdated(product.id, price);
        }
        onClose();
      } else {
        toast.error(data.detail || 'Failed to update price');
      }
    } catch (error) {
      toast.error('Failed to update price');
    } finally {
      setLoading(false);
    }
  };

  if (!product) return null;

  const currentPrice = product.price;
  const parsedNewPrice = parseFloat(newPrice) || 0;
  const savings = currentPrice - parsedNewPrice;
  const percentOff = parsedNewPrice > 0 ? Math.round((savings / currentPrice) * 100) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-minted-600" />
            Quick Price Drop
          </DialogTitle>
        </DialogHeader>

        {/* Product Info */}
        <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
          <img
            src={product.images?.[0] || 'https://via.placeholder.com/64'}
            alt={product.title}
            className="w-16 h-16 rounded-lg object-cover"
          />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate">{product.title}</p>
            <p className="text-lg font-bold text-gray-900">${currentPrice}</p>
            {product.watch_count > 0 && (
              <p className="text-xs text-minted-600 flex items-center gap-1">
                <Users className="w-3 h-3" />
                {product.watch_count} watching
              </p>
            )}
          </div>
        </div>

        {/* AI Suggestion */}
        {loadingSuggestion ? (
          <div className="flex items-center justify-center py-3">
            <Loader2 className="w-5 h-5 animate-spin text-minted-600" />
          </div>
        ) : suggestion && (
          <button
            onClick={() => setNewPrice(suggestion.suggested_price.toString())}
            className="w-full p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg text-left hover:from-purple-100 hover:to-pink-100 transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-medium text-purple-700">Suggested Price</span>
            </div>
            <p className="text-lg font-bold text-purple-800">${suggestion.suggested_price}</p>
            <p className="text-xs text-purple-600">{suggestion.reason}</p>
          </button>
        )}

        {/* Quick Drop Buttons */}
        <div className="grid grid-cols-4 gap-2">
          {[5, 10, 15, 20].map(pct => (
            <button
              key={pct}
              onClick={() => handleQuickDrop(pct)}
              className="py-2 px-3 bg-gray-100 hover:bg-minted-100 rounded-lg text-sm font-medium text-gray-700 hover:text-minted-700 transition-colors"
            >
              -{pct}%
            </button>
          ))}
        </div>

        {/* Custom Price Input */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">New Price</label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="number"
              step="0.01"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              className="pl-8 text-lg font-bold"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Savings Display */}
        {parsedNewPrice > 0 && parsedNewPrice < currentPrice && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-green-800 font-medium">
              Buyers save ${savings.toFixed(2)} ({percentOff}% off)
            </p>
          </div>
        )}

        {/* Submit */}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || parsedNewPrice >= currentPrice || parsedNewPrice <= 0}
            className="flex-1 bg-minted-600 hover:bg-minted-700"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Drop Price'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickPriceDropModal;
